"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { runMonteCarlo, type SimulationParams, type SimulationResult } from "./monteCarlo";
import { darkTheme, lightTheme, type ROITheme } from "./theme";

// ─── Helpers ─────────────────────────────────────────────────────

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
const money = (n: number) => "$" + fmt(n);

// ─── Logo SVG (inline) ───────────────────────────────────────────

function Logo() {
  return (
    <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
      <circle cx="15" cy="15" r="15.5" transform="matrix(-1 0 0 1 31 1)" stroke="url(#lg0)" />
      <path d="M19.9961 14.778C15.878 14.778 12.5879 18.3081 12.5879 22.6002C12.588 26.8923 15.878 30.4225 19.9961 30.4225C24.114 30.4223 27.4032 26.8921 27.4033 22.6002C27.4033 18.3082 24.1141 14.7782 19.9961 14.778Z" stroke="url(#lg1)" />
      <path d="M12.3975 14.778C8.27932 14.778 4.98926 18.3081 4.98926 22.6002C4.98938 26.8923 8.27939 30.4225 12.3975 30.4225C16.5154 30.4223 19.8046 26.8921 19.8047 22.6002C19.8047 18.3082 16.5154 14.7782 12.3975 14.778Z" stroke="url(#lg2)" />
      <path d="M19.9961 1.84238C15.878 1.84238 12.5879 5.3725 12.5879 9.66464C12.588 13.9567 15.878 17.4869 19.9961 17.4869C24.114 17.4867 27.4032 13.9566 27.4033 9.66464C27.4033 5.37263 24.1141 1.84258 19.9961 1.84238Z" stroke="url(#lg3)" />
      <path d="M24.0254 8.31016C19.9072 8.31016 16.6172 11.8403 16.6172 16.1324C16.6173 20.4245 19.9073 23.9547 24.0254 23.9547C28.1433 23.9545 31.4325 20.4243 31.4326 16.1324C31.4326 11.8404 28.1434 8.31036 24.0254 8.31016Z" stroke="url(#lg4)" />
      <path d="M8.36816 8.31016C4.25002 8.31016 0.959961 11.8403 0.959961 16.1324C0.960079 20.4245 4.2501 23.9547 8.36816 23.9547C12.4861 23.9545 15.7753 20.4243 15.7754 16.1324C15.7754 11.8404 12.4861 8.31036 8.36816 8.31016Z" stroke="url(#lg5)" />
      <path d="M12.3975 1.84238C8.27932 1.84238 4.98926 5.3725 4.98926 9.66464C4.98938 13.9567 8.27939 17.4869 12.3975 17.4869C16.5154 17.4867 19.8046 13.9566 19.8047 9.66464C19.8047 5.37263 16.5154 1.84258 12.3975 1.84238Z" stroke="url(#lg6)" />
      <defs>
        <linearGradient id="lg0" x1="0" y1="15" x2="30" y2="15" gradientUnits="userSpaceOnUse"><stop stopColor="#8181FF" /><stop offset="1" stopColor="#FD936E" /></linearGradient>
        <linearGradient id="lg1" x1="19.9957" y1="29.922" x2="19.9957" y2="15.278" gradientUnits="userSpaceOnUse"><stop stopColor="#FD936E" /><stop offset="1" stopColor="#8181FF" /></linearGradient>
        <linearGradient id="lg2" x1="12.3971" y1="15.278" x2="12.3971" y2="29.922" gradientUnits="userSpaceOnUse"><stop stopColor="#8181FF" /><stop offset="1" stopColor="#FD936E" /></linearGradient>
        <linearGradient id="lg3" x1="19.9957" y1="2.34238" x2="19.9957" y2="16.9864" gradientUnits="userSpaceOnUse"><stop stopColor="#FD936E" /><stop offset="1" stopColor="#8181FF" /></linearGradient>
        <linearGradient id="lg4" x1="17.1175" y1="16.1322" x2="30.9326" y2="16.1322" gradientUnits="userSpaceOnUse"><stop stopColor="#8181FF" /><stop offset="1" stopColor="#FD936E" /></linearGradient>
        <linearGradient id="lg5" x1="15.2754" y1="16.1322" x2="1.46023" y2="16.1322" gradientUnits="userSpaceOnUse"><stop stopColor="#8181FF" /><stop offset="1" stopColor="#FD936E" /></linearGradient>
        <linearGradient id="lg6" x1="12.3971" y1="2.34238" x2="12.3971" y2="16.9864" gradientUnits="userSpaceOnUse"><stop stopColor="#FD936E" /><stop offset="1" stopColor="#8181FF" /></linearGradient>
      </defs>
    </svg>
  );
}

// ─── Histogram Canvas ────────────────────────────────────────────

function Histogram({ counts, edges, median, t }: { counts: number[]; edges: number[]; median: number; t: ROITheme }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; rangeStart: number; rangeEnd: number; count: number; pct: number } | null>(null);
  const layoutRef = useRef<{ L: number; T: number; cW: number; cH: number; bW: number; n: number } | null>(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.parentElement!.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.clearRect(0, 0, W, H);

    const mx = Math.max(...counts);
    const n = counts.length;
    const L = 40, R = 10, T2 = 5, B = 18;
    const cW = W - L - R, cH = H - T2 - B, bW = cW / n;
    layoutRef.current = { L, T: T2, cW, cH, bW, n };

    ctx.strokeStyle = t.gridLine;
    ctx.lineWidth = 1;
    for (let i = 0; i <= 3; i++) { const y = T2 + cH * i / 3; ctx.beginPath(); ctx.moveTo(L, y); ctx.lineTo(W - R, y); ctx.stroke(); }

    for (let i = 0; i < n; i++) {
      const bH = (counts[i] / mx) * cH;
      const x = L + i * bW;
      const y = T2 + cH - bH;
      const g = ctx.createLinearGradient(x, y, x + bW, y + bH);
      g.addColorStop(0, t.barGradientStart);
      g.addColorStop(1, t.barGradientEnd);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.roundRect(x + 0.5, y, Math.max(bW - 1, 1), bH, 2);
      ctx.fill();
    }

    const minE = edges[0], maxE = edges[edges.length - 1];
    const mX = L + ((median - minE) / (maxE - minE)) * cW;
    ctx.strokeStyle = "#10b981";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 3]);
    ctx.beginPath(); ctx.moveTo(mX, T2); ctx.lineTo(mX, T2 + cH); ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = t.axisLabel;
    ctx.font = "9px 'Public Sans', sans-serif";
    const step = Math.max(1, Math.floor(n / 5));
    for (let i = 0; i <= n; i += step) {
      ctx.fillText("$" + Math.round(edges[i] / 1000) + "K", L + i * bW, H - 3);
    }
  }, [counts, edges, median, t]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current;
    const layout = layoutRef.current;
    if (!c || !layout) return;
    const rect = c.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const { L, T, cH, bW, n } = layout;

    const barIndex = Math.floor((mouseX - L) / bW);
    if (barIndex < 0 || barIndex >= n || mouseX < L || mouseY < T || mouseY > T + cH) {
      setTooltip(null);
      return;
    }

    const totalCount = counts.reduce((a, b) => a + b, 0);
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 10,
      rangeStart: edges[barIndex],
      rangeEnd: edges[barIndex + 1],
      count: counts[barIndex],
      pct: (counts[barIndex] / totalCount) * 100,
    });
  };

  return (
    <div style={{ position: "absolute", inset: 0 }} onMouseLeave={() => setTooltip(null)}>
      <canvas ref={canvasRef} onMouseMove={handleMouseMove} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "crosshair" }} />
      {tooltip && (
        <div style={{
          position: "absolute",
          left: tooltip.x,
          top: tooltip.y,
          transform: "translate(-50%, -100%)",
          background: t.bgCard,
          border: `1px solid ${t.border}`,
          borderRadius: 8,
          padding: "0.4rem 0.6rem",
          fontSize: "0.7rem",
          color: t.text,
          whiteSpace: "nowrap",
          pointerEvents: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 10,
        }}>
          <div style={{ fontWeight: 700, marginBottom: "0.15rem" }}>{money(tooltip.rangeStart)} – {money(tooltip.rangeEnd)}</div>
          <div style={{ color: t.textSec }}>{tooltip.count.toLocaleString()} trials ({tooltip.pct.toFixed(1)}%)</div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────

export default function ROICalculator() {
  const [mode, setMode] = useState<"dark" | "light">("dark");
  const [formulaOpen, setFormulaOpen] = useState(false);
  const t: ROITheme = mode === "dark" ? darkTheme : lightTheme;

  const [params, setParams] = useState<SimulationParams>({
    registration: 0.85, diy: 0.50, optIn: 0.70, conversion: 0.05, units: 24000, upsell: 150, share: 0.15,
  });
  const [result, setResult] = useState<SimulationResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const simulate = useCallback(() => { setResult(runMonteCarlo(params)); }, [params]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(simulate, 120);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [simulate]);

  const update = (key: keyof SimulationParams, value: number) => setParams(prev => ({ ...prev, [key]: value }));
  const r = result;

  return (
    <div style={{ height: "100vh", overflow: "hidden", display: "grid", gridTemplateRows: "auto 1fr auto", gridTemplateColumns: "25% 75%", gridTemplateAreas: '"header header" "sidebar main" "footer footer"', background: t.bg, color: t.text, fontFamily: "'Public Sans', 'DM Sans', sans-serif", transition: "background 0.3s, color 0.3s" }}>
      {/* Header */}
      <header style={{ gridArea: "header", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 1.5rem", borderBottom: `1px solid ${t.borderHeader}`, background: t.bg, transition: "background 0.3s, border-color 0.3s" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Logo />
          <h1 style={{ fontSize: "1.125rem", fontWeight: 600, color: t.textTitle, letterSpacing: "-0.01em" }}>Revenue Projection Model</h1>
          <span style={{ fontSize: "0.75rem", color: t.textMuted }}>Monte Carlo · 10,000 trials</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", background: t.badgeBg, border: `1px solid ${t.badgeBorder}`, borderRadius: 12, padding: "0.2rem 0.65rem", fontSize: "0.65rem", color: t.badgeText }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: t.badgeDot, animation: "pulse-d 2s ease-in-out infinite" }} />
            Live Simulation
          </span>
          {/* Theme Toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", background: mode === "dark" ? "rgba(255,255,255,0.06)" : "#f5f5f5", border: `1px solid ${t.border}`, borderRadius: 8, padding: "0.2rem" }}>
            <button onClick={() => setMode("dark")} style={{ all: "unset", cursor: "pointer", padding: "0.2rem 0.5rem", borderRadius: 6, fontSize: "0.7rem", fontWeight: 600, color: mode === "dark" ? "#fff" : "#999", background: mode === "dark" ? "#3b82f6" : "transparent", transition: "all 0.2s" }}>Dark</button>
            <button onClick={() => setMode("light")} style={{ all: "unset", cursor: "pointer", padding: "0.2rem 0.5rem", borderRadius: 6, fontSize: "0.7rem", fontWeight: 600, color: mode === "light" ? "#fff" : "#999", background: mode === "light" ? "linear-gradient(90deg, #8181FF, #FD936E)" : "transparent", transition: "all 0.2s" }}>Light</button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside style={{ gridArea: "sidebar", background: t.bgSidebar, borderRight: `1px solid ${t.border}`, padding: "1rem", display: "flex", flexDirection: "column", gap: "0.6rem", overflowY: "auto", transition: "background 0.3s, border-color 0.3s" }}>
        <SliderItem t={t} label="Registration Rate" value={`${Math.round(params.registration * 100)}%`} note="Total registrations ÷ units shipped. Gated manual drives 70–80% baseline. Simulated ±10%." min={0} max={100} step={1} current={params.registration * 100} onChange={v => update("registration", v / 100)} />
        <SliderItem t={t} label="DIY Percentage" value={`${Math.round(params.diy * 100)}%`} note="% of buyers who self-assemble vs. hiring a contractor. Source: support call patterns or installer data. Simulated ±10%." min={0} max={100} step={1} current={params.diy * 100} onChange={v => update("diy", v / 100)} />
        <SliderItem t={t} label="Opt-In Rate" value={`${Math.round(params.optIn * 100)}%`} note="% of registered users who consent to email/SMS marketing. Not all registrants opt in. Default 70%. Simulated ±10%." min={0} max={100} step={1} current={params.optIn * 100} onChange={v => update("optIn", v / 100)} />
        <SliderItem t={t} label="Conversion Rate" value={`${(params.conversion * 100).toFixed(1)}%`} note="% of opted-in customers who purchase within 12 months via email, SMS, or in-app offers. Highest uncertainty variable. Simulated ±40%." min={1} max={1000} step={1} current={params.conversion * 1000} onChange={v => update("conversion", v / 1000)} />
        <SliderItem t={t} label="Units / Year" value={fmt(params.units)} note="Annual units shipped through all channels (retail, Amazon, distributors). Source: your sales data. Simulated ±15%." min={1000} max={200000} step={1000} current={params.units} onChange={v => update("units", v)} />
        <SliderItem t={t} label="Avg Order Value" value={`$${fmt(params.upsell)}`} note="Average revenue per upsell transaction. Blended across replacement parts ($30–$80) and accessories ($80–$200). Simulated ±25%." min={20} max={500} step={5} current={params.upsell} onChange={v => update("upsell", v)} />
        <SliderItem t={t} label="Revenue Share" value={`${Math.round(params.share * 100)}%`} note="Metaphysical Labs' contractual share of attributed upsell revenue. Fixed — no variance in simulation." min={0} max={100} step={1} current={params.share * 100} onChange={v => update("share", v / 100)} />
      </aside>

      {/* Main */}
      <main style={{ gridArea: "main", padding: "0.75rem 1.25rem", display: "grid", gridTemplateRows: "auto 1fr auto", gap: "0.6rem", overflow: "hidden", background: t.bg, transition: "background 0.3s" }}>
        {r && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "0.6rem" }}>
            <KpiCard t={t} label="Expected (Median)" value={money(r.percentiles["50"])} sub={`${money(r.monthlyPercentiles["50"])}/mo`} featured />
            <KpiCard t={t} label="Conservative (P25)" value={money(r.percentiles["25"])} sub={`${money(r.monthlyPercentiles["25"])}/mo`} />
            <KpiCard t={t} label="Upside (P75)" value={money(r.percentiles["75"])} sub={`${money(r.monthlyPercentiles["75"])}/mo`} />
          </div>
        )}

        {r && (
          <div style={{ display: "grid", gridTemplateColumns: "65% 35%", gap: "0.6rem", minHeight: 0 }}>
            <div style={{ border: `1px solid ${t.border}`, borderRadius: 10, padding: "0.6rem 0.75rem", display: "flex", flexDirection: "column", minHeight: 0, background: t.bgCard, transition: "background 0.3s, border-color 0.3s" }}>
              <span style={{ fontSize: "0.6rem", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.3rem" }}>Annual Revenue Distribution</span>
              <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.4rem" }}>
                {(["5", "25", "50", "75", "95"] as const).map((k, i) => (
                  <div key={k} style={{ flex: 1, textAlign: "center", padding: "0.3rem 0", borderRadius: 6, background: k === "50" ? t.bgChipMedian : t.bgChip, border: k === "50" ? t.borderChipMedian : "none" }}>
                    <span style={{ fontSize: "0.55rem", color: t.textMuted, display: "block" }}>{["5th", "25th", "Median", "75th", "95th"][i]}</span>
                    <span style={{ fontSize: "0.8rem", fontWeight: 700, color: k === "50" ? t.textAccent : t.text }}>{money(r.percentiles[k])}</span>
                  </div>
                ))}
              </div>
              <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
                <Histogram counts={r.histCounts} edges={r.histEdges} median={r.percentiles["50"]} t={t} />
              </div>
            </div>

            <div style={{ border: `1px solid ${t.border}`, borderRadius: 10, padding: "0.6rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.4rem", background: t.bgCard, transition: "background 0.3s, border-color 0.3s" }}>
              <span style={{ fontSize: "0.6rem", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.2rem" }}>Confidence Levels</span>
              {[
                { label: ">$5K/yr", value: r.probAbove5k },
                { label: ">$10K/yr", value: r.probAbove10k },
                { label: ">$25K/yr", value: r.probAbove25k },
                { label: ">$50K/yr", value: r.probAbove50k },
              ].map(p => (
                <div key={p.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0.6rem", borderRadius: 6, background: t.confItemBg }}>
                  <span style={{ fontSize: "0.75rem", color: t.textSec }}>{p.label}</span>
                  <span style={{ fontSize: "1.1rem", fontWeight: 800, color: p.value > 70 ? "#4ade80" : p.value > 30 ? "#f59e0b" : "#ef4444" }}>{p.value.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {r && (
          <div style={{ border: `1px solid ${t.border}`, borderRadius: 10, padding: "0.5rem 0.75rem", display: "flex", flexDirection: "column", gap: "0.25rem", background: t.bgCard, transition: "background 0.3s, border-color 0.3s" }}>
            <span style={{ fontSize: "0.6rem", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "0.15rem" }}>Customer Funnel (Median)</span>
            {[
              { label: "Units/Year", value: params.units, isMoney: false },
              { label: "DIY Segment", value: r.medianDiyCustomers, isMoney: false },
              { label: "Registered", value: r.medianRegistered, isMoney: false },
              { label: "Opted-In", value: r.medianOptedIn, isMoney: false },
              { label: "Purchasers", value: r.medianPurchasers, isMoney: false },
              { label: "Gross Revenue", value: r.medianUpsellRevenue, isMoney: true },
            ].map(step => (
              <div key={step.label} style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <span style={{ fontSize: "0.7rem", color: t.textSec, minWidth: 90, textAlign: "right" }}>{step.label}</span>
                <div style={{ flex: 1, height: 10, background: t.funnelTrack, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.max((step.value / params.units) * 100, 0.5)}%`, background: "linear-gradient(90deg, #FD936E, #8181FF)", borderRadius: 4, transition: "width 0.4s cubic-bezier(0.16,1,0.3,1)" }} />
                </div>
                <span style={{ fontSize: "0.75rem", color: t.text, fontWeight: 600, minWidth: 70 }}>{step.isMoney ? money(step.value) : fmt(step.value)}</span>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ gridArea: "footer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.4rem 1.5rem", borderTop: `1px solid ${t.borderHeader}`, background: t.bg, transition: "background 0.3s, border-color 0.3s" }}>
        <span style={{ fontSize: "0.65rem", color: t.textMuted }}>Confidential — For Discussion Purposes Only</span>
        <button onClick={() => setFormulaOpen(true)} style={{ all: "unset", cursor: "pointer", fontSize: "0.65rem", color: t.textSec, fontFamily: "'SF Mono', monospace", background: t.codeBg, padding: "0.3rem 0.75rem", borderRadius: 6, border: `1px solid ${t.border}`, transition: "border-color 0.2s" }} onMouseEnter={e => (e.currentTarget.style.borderColor = t.textAccent)} onMouseLeave={e => (e.currentTarget.style.borderColor = t.border)}>
          Revenue = Units × DIY% × Reg% × OptIn% × Conv% × AOV × Share ↗
        </button>
      </footer>

      {/* Formula explanation modal */}
      {formulaOpen && <FormulaModal t={t} onClose={() => setFormulaOpen(false)} />}

      <style>{`@keyframes pulse-d { 0%,100%{opacity:1}50%{opacity:0.3} }`}</style>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function SliderItem({ t, label, value, note, min, max, step, current, onChange }: { t: ROITheme; label: string; value: string; note: string; min: number; max: number; step: number; current: number; onChange: (v: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setEditValue(value.replace(/[$,%\s]/g, "").replace(/,/g, ""));
    setEditing(true);
  };
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);
  const commitEdit = () => { setEditing(false); const parsed = parseFloat(editValue); if (!isNaN(parsed) && isFinite(parsed)) onChange(Math.max(min, Math.min(max, parsed))); };
  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditing(false); };

  return (
    <div style={{ border: `1px solid ${t.borderSlider}`, borderRadius: 8, padding: "0.6rem 0.75rem", transition: "border-color 0.3s" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.35rem" }}>
        <span style={{ fontSize: "0.7rem", color: t.textSec, fontWeight: 500 }}>{label}</span>
        {editing ? (
          <input ref={inputRef} type="text" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={commitEdit} onKeyDown={handleKeyDown}
            style={{ width: "4.5rem", fontSize: "0.85rem", fontWeight: 700, color: t.text, background: t.editInputBg, border: `1px solid ${t.editInputBorder}`, borderRadius: 4, padding: "0.1rem 0.3rem", textAlign: "right", outline: "none", fontFamily: "inherit" }} />
        ) : (
          <span onClick={startEditing} title="Click to edit" style={{ fontSize: "0.85rem", fontWeight: 700, color: t.text, cursor: "pointer", padding: "0.1rem 0.3rem", borderRadius: 4, transition: "background 0.15s" }}
            onMouseEnter={e => (e.currentTarget.style.background = t.hoverBg)} onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>{value}</span>
        )}
      </div>
      <input type="range" min={min} max={max} step={step} value={current} onChange={e => onChange(Number(e.target.value))} style={{ width: "100%", height: 3, WebkitAppearance: "none", appearance: "none" as any, background: t.sliderTrack, borderRadius: 2, outline: "none", cursor: "pointer" }} />
      <div style={{ fontSize: "0.6rem", color: t.textMuted, marginTop: "0.25rem" }}>{note}</div>
    </div>
  );
}

function KpiCard({ t, label, value, sub, featured }: { t: ROITheme; label: string; value: string; sub: string; featured?: boolean }) {
  return (
    <div style={{ border: `1px solid ${featured ? t.borderFeatured : t.border}`, borderRadius: 10, padding: "0.7rem 1rem", display: "flex", flexDirection: "column", alignItems: "center", background: t.bgCard, transition: "background 0.3s, border-color 0.3s" }}>
      <span style={{ fontSize: "0.6rem", color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
      <span style={{ fontSize: "1.4rem", fontWeight: 800, color: featured ? t.textAccent : t.text, margin: "0.1rem 0", letterSpacing: "-0.02em", fontFamily: "'Poppins', 'Public Sans', sans-serif" }}>{value}</span>
      <span style={{ fontSize: "0.7rem", color: t.textSec }}>{sub}</span>
    </div>
  );
}


function FormulaModal({ t, onClose }: { t: ROITheme; onClose: () => void }) {
  const metrics = [
    { symbol: "Units/Year", title: "Annual Unit Volume", desc: "Total units shipped through all channels (retail, Amazon, distributors) during the measurement period. This is your known sales data — the most reliable input in the model." },
    { symbol: "DIY%", title: "DIY Percentage", desc: "The percentage of buyers who self-assemble the product rather than hiring a contractor. Source: your support call patterns, installer network data, or product category norms. Products requiring specialized tools or permits tend toward lower DIY rates." },
    { symbol: "Reg%", title: "Registration Rate", desc: "Total product registrations divided by total units shipped. Because the assembly guide is gated behind account creation, this rate is structurally high (70–80% range). The 15–30% who don't register include contractors who already know the process, customers who find workarounds, and returns." },
    { symbol: "OptIn%", title: "Opt-In Rate", desc: "The percentage of registered customers who grant explicit consent to receive marketing communications (email, SMS, or both). Not everyone who registers will agree to promotional messages. We default to 70% — the remaining 30% decline the checkbox, provide invalid info, or unsubscribe immediately." },
    { symbol: "Conv%", title: "Conversion Rate", desc: "The percentage of opted-in customers who make at least one purchase within 12 months through any channel (email, SMS, or in-app). This is the highest-uncertainty variable — it depends on offer quality, timing, and product catalog depth. Industry benchmarks for lifecycle marketing range from 3–8%. This is what we optimize during the pilot." },
    { symbol: "AOV", title: "Average Order Value", desc: "The average revenue per upsell transaction. Blended across replacement parts ($30–$80), accessories ($80–$200), and premium services. Derived from your existing parts and accessories price list. Higher-value products (motors, screens, structural components) pull this number up." },
    { symbol: "Share", title: "Revenue Share", desc: "Metaphysical Labs' contractual percentage of attributed upsell revenue. This is a fixed agreement — it does not vary in the simulation. Applies only to incremental revenue generated through the platform that would not have existed otherwise." },
  ];

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: "2rem", maxWidth: 600, width: "90vw", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: t.text, margin: 0 }}>Revenue Formula Breakdown</h2>
          <button onClick={onClose} style={{ all: "unset", cursor: "pointer", fontSize: "1.2rem", color: t.textMuted, padding: "0.25rem" }}>✕</button>
        </div>

        <div style={{ fontFamily: "'SF Mono', monospace", fontSize: "0.8rem", color: t.textAccent, background: t.codeBg, padding: "0.75rem 1rem", borderRadius: 8, marginBottom: "1.5rem", textAlign: "center", letterSpacing: "0.02em" }}>
          Revenue = Units × DIY% × Reg% × OptIn% × Conv% × AOV × Share
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {metrics.map(m => (
            <div key={m.symbol} style={{ borderBottom: `1px solid ${t.border}`, paddingBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", marginBottom: "0.3rem" }}>
                <span style={{ fontFamily: "'SF Mono', monospace", fontSize: "0.75rem", color: t.textAccent, background: t.bgChip, padding: "0.15rem 0.5rem", borderRadius: 4, fontWeight: 600 }}>{m.symbol}</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: t.text }}>{m.title}</span>
              </div>
              <p style={{ fontSize: "0.8rem", color: t.textSec, lineHeight: 1.5, margin: 0 }}>{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
